import { useState } from 'react';
import { Banknote, CreditCard, Smartphone, Bike } from 'lucide-react';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 141 — *"Le agregué algo al que ya pagó."*
 *
 * Aparece cuando un pedido cobrado recibe productos nuevos: el cliente llamó antes de que saliera
 * la moto, o está esperando su bolsa y le llegó visita. Sólo se cobra **la diferencia**, y el
 * importe no se teclea: lo calcula el sistema restando lo pagado del total.
 *
 * Las dos salidas son las que pidió el negocio: *"a como el cliente prefiera — si me quiere
 * transferir, o le cobro la diferencia contra entrega"*.
 */

type Metodo = 'efectivo' | 'tarjeta' | 'transferencia';

const METODOS: Array<{ id: Metodo; etiqueta: string; Icono: typeof Banknote }> = [
  { id: 'efectivo', etiqueta: 'Efectivo', Icono: Banknote },
  { id: 'tarjeta', etiqueta: 'Tarjeta', Icono: CreditCard },
  { id: 'transferencia', etiqueta: 'Transferencia', Icono: Smartphone },
];

interface Props {
  abierto: boolean;
  pedidoId: number;
  /** Cuánto falta, en centavos. Lo calcula el servidor. */
  falta: number;
  /** Sólo un domicilio que no haya salido puede cobrarse en la puerta. */
  puedeCobrarAlEntregar: boolean;
  onCerrar: () => void;
  onCobrado: () => void;
}

export function CobrarDiferenciaModal({
  abierto,
  pedidoId,
  falta,
  puedeCobrarAlEntregar,
  onCerrar,
  onCobrado,
}: Props) {
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const [metodo, setMetodo] = useState<Metodo>('efectivo');
  const [referencia, setReferencia] = useState('');
  const [recibido, setRecibido] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!abierto) return null;

  const recibidoCentavos = Math.round((Number(recibido.replace(',', '.')) || 0) * 100);
  const cambio = recibidoCentavos > falta ? recibidoCentavos - falta : 0;

  const enviar = async (alEntregar: boolean) => {
    setGuardando(true);
    setError(null);
    const res = await window.api.pedidos.cobrarDiferencia({
      pedidoId,
      alEntregar,
      pagos: alEntregar
        ? []
        : [
            {
              metodo,
              monto: falta,
              referencia: referencia.trim() || undefined,
              ...(metodo === 'efectivo' && recibidoCentavos > falta
                ? { recibido: recibidoCentavos }
                : {}),
            },
          ],
    });
    setGuardando(false);
    if (res.ok) {
      onCobrado();
      onCerrar();
      return;
    }
    setError(traducirErrorBackend(res.error, res.codigo));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <header className="border-b border-slate-200 px-5 py-3">
          <h2 className="text-lg font-semibold">Cobrar lo que falta</h2>
          <p className="text-sm text-slate-600">
            Se le agregaron productos después de cobrarle.
          </p>
        </header>

        <div className="space-y-4 px-5 py-4">
          {/* El número grande: es lo único que hay que mirar para cobrar. */}
          <div className="rounded-xl bg-slate-100 px-4 py-3 text-center">
            <p className="text-xs font-semibold uppercase text-slate-600">Falta cobrar</p>
            <p className="font-mono text-3xl font-bold">
              {formatPrecio(falta, monedaSimbolo, locale)}
            </p>
          </div>

          <div>
            <span className="label">¿Con qué paga?</span>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {METODOS.map(({ id, etiqueta, Icono }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMetodo(id)}
                  aria-pressed={metodo === id}
                  className={`flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-lg border-2 text-sm font-semibold ${
                    metodo === id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-slate-300 bg-white text-slate-700'
                  }`}
                >
                  <Icono className="h-5 w-5" />
                  {etiqueta}
                </button>
              ))}
            </div>
          </div>

          {metodo === 'efectivo' ? (
            <div>
              <label className="label" htmlFor="recibidoDif">
                ¿Con cuánto paga? <span className="font-normal text-slate-600">(opcional)</span>
              </label>
              <input
                id="recibidoDif"
                className="input"
                inputMode="decimal"
                value={recibido}
                onChange={(e) => setRecibido(e.target.value)}
                placeholder={(falta / 100).toFixed(2)}
              />
              {cambio > 0 && (
                <div className="mt-2 rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2 text-center">
                  <p className="text-xs font-semibold uppercase text-emerald-800">
                    Cambio a entregar
                  </p>
                  <p className="font-mono text-xl font-bold text-emerald-900">
                    {formatPrecio(cambio, monedaSimbolo, locale)}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="label" htmlFor="refDif">
                Referencia o últimos 4 dígitos{' '}
                <span className="font-normal text-slate-600">(opcional)</span>
              </label>
              <input
                id="refDif"
                className="input"
                value={referencia}
                maxLength={100}
                onChange={(e) => setReferencia(e.target.value)}
              />
            </div>
          )}

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
              {error}
            </p>
          )}
        </div>

        <footer className="space-y-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={() => void enviar(false)}
            disabled={guardando}
            className="btn-primary min-h-[44px] w-full disabled:opacity-50"
          >
            {guardando ? 'Cobrando…' : `Cobrar ${formatPrecio(falta, monedaSimbolo, locale)}`}
          </button>

          {/*
            Sprint 141 — La otra salida, y la pidió el negocio con estas palabras: *"a como el
            cliente prefiera… o le cobro la diferencia contra entrega"*. No registra dinero: sólo
            deja escrito que el pedido sale a cobrar y quién lo autorizó.
          */}
          {puedeCobrarAlEntregar && (
            <button
              type="button"
              onClick={() => void enviar(true)}
              disabled={guardando}
              className="btn-ghost min-h-[44px] w-full gap-2 border border-amber-300 bg-amber-50 text-amber-900"
            >
              <Bike className="h-4 w-4" />
              Que la cobre el repartidor al entregar
            </button>
          )}

          <button type="button" onClick={onCerrar} className="btn-ghost min-h-[44px] w-full">
            Ahora no
          </button>
        </footer>
      </div>
    </div>
  );
}
